import { useContext } from 'react'
import swal from 'sweetalert2'

import {
  Box, Button, Columns, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useProcessChecklist from './hooks/useProcessChecklist'

import Assets from './components/Assets'
import ChecklistScore from './components/ChecklistScore'
import ChecklistUser from './components/ChecklistUser'
import FailedTaskListItems from './components/FailedTaskListItems'
import Links from './components/Links'
import TaskListItems from './components/TaskListItems'

const MODAL_KEY = 'ViewAndProcessChecklistModal'

type UseProcessChecklistPayload = ReturnType<typeof useProcessChecklist>

type ModalCallbacks = {
  closeModal: () => void,
  toggleTaskListCompletion: (payload: HandleCallbackActionParams) => void,
}

type ConfirmCompletionParams = {
  callbacks: ModalCallbacks,
  submitChecklistFn: UseProcessChecklistPayload['callbacks']['submitChecklist'],
}

const confirmCompletion = (params: ConfirmCompletionParams) => {
  const { callbacks, submitChecklistFn } = params
  const { closeModal, toggleTaskListCompletion } = callbacks

  const toggleTaskListCompletionPayload = {
    callbacks: {
      action: submitChecklistFn,
      afterAction: closeModal,
    },
    toastText: 'Checklist Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: 'Yes, submit it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Submit Checklist?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        toggleTaskListCompletion(toggleTaskListCompletionPayload)
      }
    })
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ViewAndProcessChecklistModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { taskList: initTaskList } = modalPayload

  const processChecklistPayload = useProcessChecklist({ taskListId: initTaskList?.id })
  const {
    activeStepKey,
    callbacks: { goToStep, submitChecklist: submitChecklistFn },
    loadingTaskListItems,
    taskList,
    taskList: { completed },
    taskListPayload: {
      updating,
    },
  } = processChecklistPayload

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} size="fullscreen" showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Process" titleSecondLine="Checklist" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={!completed}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loadingTaskListItems} times={3} />

            {activeStepKey === 'overview' && <TaskListItems processChecklistPayload={processChecklistPayload} />}
            {activeStepKey === 'summary' && <FailedTaskListItems processChecklistPayload={processChecklistPayload} />}
          </Columns.Main>

          <Columns.Sidebar>
            <ChecklistScore processChecklistPayload={processChecklistPayload} />
            {/*
             // @ts-expect-error TS2322 */}
            {completed && <ChecklistUser taskList={taskList} />}
            {/*
             // @ts-expect-error TS2322 */}
            <Assets taskList={taskList} />
            {/*
             // @ts-expect-error TS2322 */}
            <Links taskList={taskList} />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      {!completed && (
        <SidebarModal.Footer>
          <Box marginLeft="auto" width="auto">
            {activeStepKey === 'summary' && (
              <>
                <Button
                  buttonStyle="secondaryEdit"
                  loadingBubbleColor="white"
                  onClick={() => goToStep('overview')}
                  size="large"
                  // @ts-expect-error TS2322
                  width="auto"
                >
                  Go Back
                </Button>

                <Button
                  buttonStyle="primaryCreate"
                  loadingBubbleColor="white"
                  loading={updating}
                  onClick={() => confirmCompletion({ callbacks, submitChecklistFn })}
                  // @ts-expect-error TS2322
                  marginLeft="medium"
                  size="large"
                  width="auto"
                >
                  Submit Checklist
                </Button>
              </>
            )}

            {activeStepKey === 'overview' && (
              <Button
                buttonStyle="primaryEdit"
                loadingBubbleColor="white"
                onClick={() => goToStep('summary')}
                size="large"
                // @ts-expect-error TS2322
                width="auto"
              >
                Next Step
              </Button>
            )}
          </Box>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  // @ts-expect-error TS2786
  <SidebarModal.RenderController {...props}>
    <ViewAndProcessChecklistModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
